import fetchApi from '@/lib/tools/api'
import {CheckCircle as SuccessIcon, Error as ErrorIcon, Store as StoreIcon, Sync as SyncIcon} from '@mui/icons-material'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	LinearProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography
} from '@mui/material'
import React, {useCallback, useState} from 'react'
import {useListContext, useNotify, useRefresh, useUnselectAll} from 'react-admin'

interface SyncResult {
	id: number
	name: string
	siret?: string
	success: boolean
	message?: string
}

interface BulkSyncResponse {
	success: boolean
	results: SyncResult[]
	total: number
	successful: number
	failed: number
}

export default function BulkSireneSyncButton() {
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [progress, setProgress] = useState(0)
	const [results, setResults] = useState<SyncResult[]>([])
	const [syncStats, setSyncStats] = useState<{ successful: number; failed: number; total: number } | null>(null)

	const {selectedIds, data} = useListContext()
	const notify = useNotify()
	const refresh = useRefresh()
	const unselectAll = useUnselectAll('producers/stores')

	const selectedStores = selectedIds.map(id =>
		data?.find((store: any) => store.id === id)
	).filter(Boolean)

	// Only show stores with SIRET for synchronization
	const validStores = selectedStores.filter((store: any) => store.siret)

	const handleClickOpen = useCallback(() => {
		setOpen(true)
		setResults([])
		setSyncStats(null)
		setProgress(0)
	}, [])

	const handleClose = useCallback(() => {
		setOpen(false)
		setResults([])
		setSyncStats(null)
		setProgress(0)
		if (syncStats && syncStats.successful > 0) {
			refresh()
			unselectAll()
		}
	}, [refresh, unselectAll, syncStats])

	const handleBulkSync = useCallback(async () => {
		if (!validStores.length) return

		setLoading(true)
		setProgress(0)
		setResults([])
		setSyncStats(null)

		try {
			// Call the backend API to trigger bulk SIRENE sync for stores
			const response = await fetchApi<BulkSyncResponse>(
				`/producers/stores/bulk-sync-sirene`,
				{store_ids: validStores.map((store: any) => store.id)},
				null,
				'POST'
			)

			setResults(response.results)
			setSyncStats({
				total: response.total,
				successful: response.successful,
				failed: response.failed
			})

			setProgress(100)

			if (response.successful > 0) {
				notify(
					`${response.successful} établissement${response.successful > 1 ? 's' : ''} synchronisé${response.successful > 1 ? 's' : ''} avec succès`,
					{type: 'success'}
				)
			}

			if (response.failed > 0) {
				notify(
					`${response.failed} synchronisation${response.failed > 1 ? 's' : ''} échouée${response.failed > 1 ? 's' : ''}`,
					{type: 'warning'}
				)
			}

		} catch (error: any) {
			const errorMessage = error.message || 'Erreur lors de la synchronisation en lot'
			notify(`Échec de la synchronisation: ${errorMessage}`, {type: 'error'})
			setSyncStats({
				total: validStores.length,
				successful: 0,
				failed: validStores.length
			})
		} finally {
			setLoading(false)
		}
	}, [validStores, notify])

	if (!validStores.length) {
		return null
	}

	const isCompleted = syncStats !== null
	const successfulResults = results.filter(r => r.success)
	const failedResults = results.filter(r => !r.success)
	const storesWithoutSiret = selectedStores.filter((store: any) => !store.siret)

	return (
		<>
			<Button
				variant="contained"
				color="primary"
				startIcon={<SyncIcon/>}
				onClick={handleClickOpen}
				disabled={loading}
				size={'small'}
				sx={{
					ml: 1,
					'&:hover': {
						transform: 'scale(1.02)',
						transition: 'transform 0.2s ease-in-out'
					}
				}}
			>
				Synchroniser SIRENE ({validStores.length})
			</Button>

			<Dialog
				open={open}
				onClose={loading ? undefined : handleClose}
				maxWidth="md"
				fullWidth
				aria-labelledby="bulk-sirene-sync-dialog-title"
				disableEscapeKeyDown={loading}
			>
				<DialogTitle id="bulk-sirene-sync-dialog-title">
					<Box display="flex" alignItems="center" gap={1}>
						<SyncIcon color="primary"/>
						Synchronisation en lot INSEE SIRENE - Établissements
					</Box>
				</DialogTitle>

				<DialogContent sx={{minHeight: '300px'}}>
					{isCompleted ? (
						// Results view
						<Box>
							<Alert
								severity={syncStats.failed === 0 ? 'success' : syncStats.successful === 0 ? 'error' : 'warning'}
								sx={{mb: 3}}
							>
								<Typography variant="body1" fontWeight="bold">
									Synchronisation terminée
								</Typography>
								<Typography variant="body2">
									{syncStats.successful} succès • {syncStats.failed} échecs
									sur {syncStats.total} établissements
								</Typography>
							</Alert>

							{successfulResults.length > 0 && (
								<Box sx={{mb: 3}}>
									<Typography variant="h6" color="success.main" gutterBottom>
										<SuccessIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
										Synchronisations réussies ({successfulResults.length})
									</Typography>
									<List dense>
										{successfulResults.map((result) => (
											<ListItem key={result.id}>
												<ListItemIcon>
													<SuccessIcon color="success"/>
												</ListItemIcon>
												<ListItemText
													primary={result.name}
													secondary={`${result.siret ? `SIRET: ${result.siret} • ` : ''}${result.message || 'Données SIRENE mises à jour'}`}
												/>
											</ListItem>
										))}
									</List>
								</Box>
							)}

							{failedResults.length > 0 && (
								<Box>
									<Typography variant="h6" color="error.main" gutterBottom>
										<ErrorIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
										Synchronisations échouées ({failedResults.length})
									</Typography>
									<List dense>
										{failedResults.map((result) => (
											<ListItem key={result.id}>
												<ListItemIcon>
													<ErrorIcon color="error"/>
												</ListItemIcon>
												<ListItemText
													primary={result.name}
													secondary={`${result.siret ? `SIRET: ${result.siret} • ` : ''}${result.message || 'Erreur inconnue'}`}
												/>
											</ListItem>
										))}
									</List>
								</Box>
							)}
						</Box>
					) : loading ? (
						// Loading view
						<Box>
							<Alert severity="info" sx={{mb: 3}}>
								<Box display="flex" alignItems="center" gap={2}>
									<CircularProgress size={20}/>
									<Typography variant="body1">
										Synchronisation en cours... ({validStores.length} établissements)
									</Typography>
								</Box>
							</Alert>

							<Box sx={{mb: 2}}>
								<LinearProgress
									variant="indeterminate"
									sx={{
										height: 8,
										borderRadius: 4,
										mb: 1
									}}
								/>
								<Typography variant="body2" color="text.secondary" align="center">
									Traitement des données SIRENE... Cela peut prendre plusieurs minutes.
								</Typography>
							</Box>

							<Typography variant="body2" sx={{mt: 2}}>
								Établissements sélectionnés :
							</Typography>
							<Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1}}>
								{validStores.slice(0, 10).map((store: any) => (
									<Chip
										key={store.id}
										icon={<StoreIcon/>}
										label={store.name || `ID: ${store.id}`}
										size="small"
										variant="outlined"
									/>
								))}
								{validStores.length > 10 && (
									<Chip
										label={`... et ${validStores.length - 10} autres`}
										size="small"
										variant="outlined"
									/>
								)}
							</Box>
						</Box>
					) : (
						// Confirmation view
						<Box>
							<Typography variant="h6" gutterBottom>
								Synchroniser {validStores.length} établissement{validStores.length > 1 ? 's' : ''}
							</Typography>

							{storesWithoutSiret.length > 0 && (
								<Alert severity="warning" sx={{mb: 2}}>
									<Typography variant="body2">
										<strong>Note
											:</strong> {storesWithoutSiret.length} établissement{storesWithoutSiret.length > 1 ? 's' : ''}
										{storesWithoutSiret.length > 1 ? ' sont exclus' : ' est exclu'} car {storesWithoutSiret.length > 1 ? 'ils n\'ont' : 'il n\'a'} pas
										de SIRET renseigné.
									</Typography>
								</Alert>
							)}

							<Alert severity="warning" sx={{mt: 2, mb: 3}}>
								<Typography variant="body2">
									<strong>Attention :</strong> Cette opération va mettre à jour les données SIRENE
									pour tous les établissements sélectionnés via leur SIRET. Les données existantes
									seront remplacées
									par celles de la base INSEE officielle.
								</Typography>
							</Alert>

							<Typography variant="body2" sx={{mb: 2}}>
								<strong>Données qui seront mises à jour :</strong>
							</Typography>
							<Box component="ul" sx={{pl: 2, m: 0, mb: 3}}>
								<Typography component="li" variant="body2">
									Statut administratif (Actif/Fermé)
								</Typography>
								<Typography component="li" variant="body2">
									Code d'activité principale (APE)
								</Typography>
								<Typography component="li" variant="body2">
									Date de cessation d'activité (si applicable)
								</Typography>
								<Typography component="li" variant="body2">
									Autres informations administratives disponibles
								</Typography>
							</Box>

							<Divider sx={{my: 2}}/>

							<Typography variant="body2" gutterBottom>
								<strong>Établissements sélectionnés avec SIRET :</strong>
							</Typography>
							<Box sx={{
								maxHeight: '200px',
								overflow: 'auto',
								border: 1,
								borderColor: 'divider',
								borderRadius: 1,
								p: 1
							}}>
								{validStores.map((store: any) => (
									<Chip
										key={store.id}
										icon={<StoreIcon/>}
										label={`${store.name || `ID: ${store.id}`} (${store.siret})`}
										size="small"
										variant="outlined"
										sx={{m: 0.5}}
									/>
								))}
							</Box>

							<Alert severity="info" sx={{mt: 2}}>
								<Typography variant="body2">
									<strong>Estimation :</strong> Cette opération peut prendre plusieurs minutes
									selon le nombre d'établissements à traiter.
								</Typography>
							</Alert>
						</Box>
					)}
				</DialogContent>

				<DialogActions sx={{px: 3, pb: 2}}>
					{isCompleted ? (
						<Button onClick={handleClose} color="primary" variant="contained">
							Fermer
						</Button>
					) : (
						<>
							<Button
								onClick={handleClose}
								disabled={loading}
								color="inherit"
							>
								Annuler
							</Button>
							<Button
								onClick={handleBulkSync}
								disabled={loading}
								variant="contained"
								color="primary"
								startIcon={loading ? <CircularProgress size={20}/> : <SyncIcon/>}
							>
								{loading ? 'Synchronisation...' : 'Démarrer la synchronisation'}
							</Button>
						</>
					)}
				</DialogActions>
			</Dialog>
		</>
	)
}
